import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { MapPin } from 'lucide-react';
import { z } from 'zod';
import { useStore } from '@tanstack/react-form';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { optionalInput } from '@/common/form/optionalInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import { DialogDescription, DialogTitle } from '@/common/components/ui/dialog';
import { useCountries } from '@/features/auth/hooks/useCountries';
import { cn } from '@/lib/utils';
import { BillingAddressService } from '../../services/BillingAddressService';
import type { BillingAddress, BillingAddressOwner } from '../../services/schemas';
import type { Organization } from '@/features/organizations/services/schemas';

// ─── Constants ────────────────────────────────────────────────────────────────

const PERSONAL_SCOPE = 'personal';

// ─── Form schema ──────────────────────────────────────────────────────────────

const billingAddressFormSchema = z
  .object({
    /** 'personal' or org.id string. Only relevant when creating. */
    scopeId: z.string(),
    name: requiredInput(z.string().min(1)),
    street: requiredInput(z.string().min(1)),
    city: requiredInput(z.string().min(1)),
    zipCode: requiredInput(z.string().min(1)),
    country: requiredInput(z.string()),
    vatRegistered: z.boolean(),
    VAT_ID: optionalInput(z.string()).transform((v) => v || null),
    CRN: optionalInput(z.string()).transform((v) => v || null),
    email: requiredInput(z.email()),
  })
  .superRefine((data, ctx) => {
    if (data.vatRegistered && !data.VAT_ID) {
      ctx.addIssue({
        code: 'custom',
        path: ['VAT_ID'],
        message: t`This field is required`,
      });
    }
    if (data.scopeId !== PERSONAL_SCOPE && !data.CRN) {
      ctx.addIssue({
        code: 'custom',
        path: ['CRN'],
        message: t`This field is required`,
      });
    }
  });

// ─── Props ────────────────────────────────────────────────────────────────────

export interface BillingAddressDialogContentProps {
  address?: BillingAddress;
  /** Pre-selected owner. When provided, scope select is pre-filled and disabled. */
  owner?: BillingAddressOwner;
  /** Org list for the scope select. Personal option is always shown first. */
  orgs?: Organization[];
  onSuccess: () => void;
  onClose: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BillingAddressDialogContent({
  address,
  owner,
  orgs,
  onSuccess,
  onClose,
}: BillingAddressDialogContentProps) {
  const isEdit = address !== undefined;
  const scopeLocked = owner !== undefined;
  const countries = useCountries();

  const form = useAppForm(
    appFormOptions({
      schema: billingAddressFormSchema,
      defaultValues: isEdit
        ? {
            scopeId: address.partner !== null ? String(address.partner) : PERSONAL_SCOPE,
            name: address.name ?? null,
            street: address.street,
            city: address.city,
            zipCode: address.zipCode,
            country: address.country,
            vatRegistered: !!address.VAT_ID,
            VAT_ID: address.VAT_ID,
            CRN: address.CRN,
            email: address.email || null,
          }
        : {
            scopeId: owner?.orgId !== undefined ? String(owner.orgId) : PERSONAL_SCOPE,
            name: null,
            street: null,
            city: null,
            zipCode: null,
            country: 'CZ',
            vatRegistered: true,
            VAT_ID: null,
            CRN: null,
            email: null,
          },
      onSubmit: async ({ value }) => {
        const { scopeId, vatRegistered: _, ...addressData } = value;
        if (address) {
          await BillingAddressService.update(address.id, addressData);
        } else {
          const partner = scopeId !== PERSONAL_SCOPE ? Number(scopeId) : undefined;
          await BillingAddressService.create({
            ...addressData,
            ...(partner !== undefined ? { partner } : {}),
          });
        }
        onSuccess();
      },
    })
  );

  const scopeId = useStore(form.store, (state) => state.values.scopeId);
  const isPersonal = scopeId === PERSONAL_SCOPE;
  const vatRegistered = useStore(form.store, (state) => state.values.vatRegistered);

  return (
    <>
      <div className="flex items-center gap-3 mb-1">
        <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
          <MapPin size={18} className="text-primary" />
        </div>
        <div>
          <DialogTitle>
            {isEdit ? <Trans>Edit billing address</Trans> : <Trans>Add billing address</Trans>}
          </DialogTitle>
          <DialogDescription>
            {isPersonal ? (
              <Trans>Only visible to you.</Trans>
            ) : (
              <Trans>Shared with all organization members.</Trans>
            )}
          </DialogDescription>
        </div>
      </div>

      <form.AppForm>
        <form.FormElement>
          <FormErrors className="mb-4" />

          <div className="space-y-4">
            {!isEdit && (
              <form.AppField name="scopeId">
                {(field) => (
                  <field.SelectField label={<Trans>For</Trans>} disabled={scopeLocked} required>
                    <SelectItem value={PERSONAL_SCOPE}>
                      <Trans>Personal</Trans>
                    </SelectItem>
                    {orgs?.map((org) => (
                      <SelectItem key={org.id} value={String(org.id)}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </field.SelectField>
                )}
              </form.AppField>
            )}

            <form.AppField name="name">
              {(field) => (
                <field.TextField
                  label={<Trans>Company name</Trans>}
                  placeholder={t`Tech Solutions Ltd.`}
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="street">
              {(field) => (
                <field.TextField
                  label={<Trans>Street and number</Trans>}
                  placeholder={t`Main Street 1`}
                  required
                />
              )}
            </form.AppField>

            <div className="grid grid-cols-2 gap-4">
              <form.AppField name="city">
                {(field) => (
                  <field.TextField label={<Trans>City</Trans>} placeholder={t`Prague`} required />
                )}
              </form.AppField>

              <form.AppField name="zipCode">
                {(field) => (
                  <field.TextField
                    label={<Trans>Postal code</Trans>}
                    placeholder="110 00"
                    required
                  />
                )}
              </form.AppField>
            </div>

            <form.AppField name="country">
              {(field) => (
                <field.SelectField label={<Trans>Country</Trans>} required>
                  {countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <div className="border-t border-border pt-4 space-y-4">
              <form.AppField name="CRN">
                {(field) => (
                  <field.TextField
                    required={!isPersonal}
                    label={<Trans>Company registration number</Trans>}
                    placeholder="12345678"
                  />
                )}
              </form.AppField>

              <form.AppField name="vatRegistered">
                {(field) => (
                  <div className="space-y-1.5">
                    <span className="text-sm font-medium leading-none">
                      <Trans>VAT</Trans>
                    </span>
                    <div className="flex gap-2">
                      {([true, false] as const).map((v) => (
                        <button
                          key={String(v)}
                          type="button"
                          aria-pressed={field.state.value === v}
                          onClick={() => {
                            field.handleChange(v);
                            if (!v) form.setFieldValue('VAT_ID', null);
                          }}
                          className={cn(
                            'flex-1 rounded-xl border py-2.5 text-sm font-medium transition-colors',
                            field.state.value === v
                              ? 'border-primary bg-primary/5 text-primary'
                              : 'border-border bg-muted/50 text-muted-foreground hover:border-border/80'
                          )}
                        >
                          {v ? <Trans>VAT registered</Trans> : <Trans>Not VAT registered</Trans>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </form.AppField>

              {vatRegistered && (
                <form.AppField name="VAT_ID">
                  {(field) => (
                    <field.TextField
                      label={<Trans>VAT ID</Trans>}
                      placeholder="CZ12345678"
                      required
                    />
                  )}
                </form.AppField>
              )}

              <form.AppField name="email">
                {(field) => (
                  <field.TextField
                    label={<Trans>Contact email</Trans>}
                    type="email"
                    placeholder="billing@company.com"
                    required
                  />
                )}
              </form.AppField>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              <Trans>Cancel</Trans>
            </Button>
            <FormSubmit>
              {isEdit ? <Trans>Save changes</Trans> : <Trans>Add address</Trans>}
            </FormSubmit>
          </div>
        </form.FormElement>
      </form.AppForm>
    </>
  );
}
